import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, CheckCircle2, Loader2, LogOut, MapPin, Phone } from "lucide-react";
import { usePortalAuth } from "@/components/portal/PortalAuth";
import { PhotoUpload } from "@/components/portal/PhotoUpload";
import { SEO } from "@/components/site/SEO";

const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;

function SignaturePad({ onSave, busy }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const [name, setName] = useState("");
  const [childSafety, setChildSafety] = useState(false);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches?.[0] || e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };
  const start = (e) => {
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = "#0B1D3A";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.stroke();
    setHasInk(true);
  };
  const end = () => (drawing.current = false);
  const clear = () => {
    canvasRef.current.getContext("2d").clearRect(0, 0, 9999, 9999);
    setHasInk(false);
  };

  return (
    <div className="rounded-xl border border-warm-200 p-4">
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-2">
        Customer completion signature
      </label>
      <canvas
        ref={canvasRef}
        width={600}
        height={180}
        data-testid="signature-canvas"
        className="w-full h-36 rounded-lg border-2 border-dashed border-warm-300 bg-warm-50 touch-none cursor-crosshair"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Customer name"
        data-testid="signature-name"
        className="mt-2.5 w-full rounded-lg border border-warm-300 px-3 py-2.5 text-sm focus:border-navy-900 focus:outline-none"
      />
      <label className="mt-2.5 flex items-start gap-2.5 text-xs text-slate-600 cursor-pointer">
        <input type="checkbox" checked={childSafety} onChange={(e) => setChildSafety(e.target.checked)} data-testid="child-safety-check" className="mt-0.5 h-4 w-4 accent-[#0B1D3A]" />
        Customer advised about child-safety devices
      </label>
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={clear} className="flex-1 rounded-full border border-warm-300 py-2.5 text-xs font-semibold text-slate-600">
          Clear
        </button>
        <button
          type="button"
          data-testid="signature-save"
          disabled={!hasInk || !name.trim() || busy}
          onClick={() => onSave(canvasRef.current.toDataURL("image/png"), name, childSafety)}
          className="flex-1 rounded-full bg-emerald-600 py-2.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-40"
        >
          Complete Job
        </button>
      </div>
    </div>
  );
}

export default function InstallerView() {
  const { user, logout, api } = usePortalAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/my-jobs").then((r) => setJobs(r.data.jobs));
  useEffect(() => {
    if (user === false) navigate("/portal/login", { replace: true });
    else if (user) load().catch(() => setJobs([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (user === null)
    return <div className="min-h-screen bg-warm-50 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-navy-900" /></div>;
  if (user === false) return null;

  const complete = async (job, signature, customerName, childSafety) => {
    setBusy(true);
    try {
      await api.post(`/orders/${job.id}/complete`, {
        signature_base64: signature,
        customer_name: customerName,
        child_safety: childSafety,
      });
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-warm-50" data-testid="installer-view">
      <SEO title="My Jobs | My Blinds Company" description="Installer job sheet." path="/portal/jobs" />
      <header className="sticky top-0 z-40 bg-navy-950 text-white">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="My Blinds Company logo" className="h-9 w-auto rounded bg-white p-0.5" />
            <div>
              <p className="font-serif text-lg leading-none">My Jobs</p>
              <p className="text-[11px] text-white/50 mt-0.5">{user.name || user.email}</p>
            </div>
          </div>
          <button type="button" data-testid="installer-logout" onClick={logout} className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 text-xs font-medium hover:bg-white/10">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {jobs === null ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-navy-900" /></div>
        ) : jobs.length === 0 ? (
          <div className="rounded-2xl border border-warm-200 bg-white p-12 text-center">
            <CalendarDays className="h-8 w-8 text-warm-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No jobs assigned to you yet.</p>
          </div>
        ) : (
          jobs.map((j) => {
            const address = [j.address1, j.address2, j.town, j.postcode].filter(Boolean).join(", ");
            const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
            const done = j.status === "Installed" || j.status === "Completed" || j.status === "Balance Paid";
            return (
              <article key={j.id} data-testid={`job-${j.id}`} className="rounded-2xl border border-warm-200 bg-white shadow-sm overflow-hidden">
                <div className="bg-navy-950 text-white p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-white/50">{j.ref}</p>
                      <h2 className="font-serif text-xl mt-0.5">{j.customer}</h2>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${done ? "bg-emerald-500" : "bg-white/15"}`}>{j.status}</span>
                  </div>
                  {j.install_date && (
                    <p className="text-sm text-white/70 mt-2">
                      {new Date(j.install_date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
                      {j.install_time ? ` · ${j.install_time}` : ""}
                    </p>
                  )}
                </div>
                <div className="p-5 space-y-4">
                  <div className="grid grid-cols-2 gap-2">
                    {j.phone && (
                      <a href={`tel:${j.phone}`} data-testid={`job-call-${j.id}`} className="flex items-center justify-center gap-2 rounded-full bg-navy-900 py-3 text-sm font-semibold text-white">
                        <Phone className="h-4 w-4" /> Call
                      </a>
                    )}
                    <a href={mapsUrl} target="_blank" rel="noopener noreferrer" data-testid={`job-nav-${j.id}`} className="flex items-center justify-center gap-2 rounded-full border border-navy-900/30 py-3 text-sm font-semibold text-navy-900">
                      <MapPin className="h-4 w-4" /> Navigate
                    </a>
                  </div>
                  {address && <p className="text-sm text-slate-600">{address}</p>}
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Products to fit</h3>
                    <ul className="space-y-1.5 text-sm text-slate-700">
                      {j.items.map((i, x) => <li key={x}>— {i.description} × {i.qty}</li>)}
                    </ul>
                  </div>
                  {j.measurements?.length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Measurements</h3>
                      <div className="overflow-x-auto rounded-xl border border-warm-200">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-[10px] uppercase text-slate-400 border-b border-warm-200 bg-warm-100">
                              <th className="px-2.5 py-2">Room</th><th className="px-2.5 py-2">Type</th><th className="px-2.5 py-2">Size</th><th className="px-2.5 py-2">Fabric</th>
                            </tr>
                          </thead>
                          <tbody>
                            {j.measurements.map((m, x) => (
                              <tr key={x} className="border-b border-warm-200/60 last:border-0">
                                <td className="px-2.5 py-2">{m.room}</td>
                                <td className="px-2.5 py-2">{m.blindType}</td>
                                <td className="px-2.5 py-2 whitespace-nowrap">{m.width}×{m.drop}{m.unit}</td>
                                <td className="px-2.5 py-2">{m.material}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Job photos</h3>
                    <PhotoUpload
                      api={api}
                      attachUrl={`/orders/${j.id}/photos`}
                      photos={j.photos || []}
                      onChange={load}
                      tags={["Before", "During", "After", "Problem"]}
                    />
                  </div>
                  {j.balance_due - (j.balance_paid || 0) > 0 && (
                    <p className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
                      Balance to collect: <strong>{gbp(j.balance_due - (j.balance_paid || 0))}</strong>
                    </p>
                  )}
                  {j.completion_signature ? (
                    <p className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-900 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" /> Completed and signed by {j.completion_name}
                    </p>
                  ) : (
                    !done && <SignaturePad busy={busy} onSave={(sig, name, cs) => complete(j, sig, name, cs)} />
                  )}
                </div>
              </article>
            );
          })
        )}
      </main>
    </div>
  );
}
